import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Controller,
  ControllerRenderProps,
  FieldValues,
  RegisterOptions,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import { useShallow } from 'zustand/react/shallow';

import {
  CountryFlag,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
  cn,
} from '@column/ui';

import { FormField, FormFieldProps } from '~/components/FormField';
import { Section } from '~/components/Section';
import { Map } from '~/elements/Map';
import { useAddress } from '~/hooks/useAddress';
import { useDebounce } from '~/hooks/useDebounce';
import { AddressType } from '~/repositories';
import { LibraryStatus, useGoogleMapsStore } from '~/stores/GoogleMaps';
import { useNotificationStore } from '~/stores/Notification';

export interface AddressFieldsProps {
  /**
   * React Hook Form field-name prefix for the address object. Works at the top
   * level (`"address"`) or nested (`"entity.physicalAddress"`). Reads/writes
   * `${name}.line1`, `.line2`, `.city`, `.state`, `.postalCode`, `.countryCode`.
   * Must be rendered inside a `FormProvider`.
   */
  name: string;
  /** Restrict autocomplete + country options to the US. */
  justUS?: boolean;
  /** Restrict the country dropdown to these ISO alpha-2 codes. */
  countryCodes?: string[];
  /**
   * Fade in a Google Map preview once the address resolves: in a full-height column beside the fields on wide
   * layouts, or below the fields on narrower ones. Off by default — opt in per usage.
   */
  showMapPreview?: boolean;
  /** Apply RHF `required` rules to line 1, city and country (+ state/postal when `justUS`). */
  required?: boolean;
  /** Names the address in required messages ("Please fill out the <errorLabel> City field"). */
  errorLabel?: string;
  /** Section headline shown above the fields. Defaults to "Address"; pass `null` to omit. */
  heading?: React.ReactNode;
  disabled?: boolean;
  /** Collapses the section and dims its headline, like other `RenderFields` sections. */
  isDisabled?: boolean;
  /** Extra rules per field, merged over the defaults (e.g. a country-specific postal code format). */
  fieldRules?: Partial<Record<keyof AddressType, RegisterOptions>>;
}

// Region term used in the "City / <term>" row label + the region placeholder,
// switched by the selected country. Defaults to "State".
const REGION_TERMS: Record<string, string> = {
  US: 'State',
  CA: 'Province',
  GB: 'County',
  IE: 'County',
  AU: 'State',
  IN: 'State',
  MX: 'State',
  BR: 'State',
  DE: 'State',
  IT: 'Province',
  ES: 'Province',
  NL: 'Province',
  JP: 'Prefecture',
  CN: 'Province',
};

const regionTerm = (country: string): string => {
  if (!country) return 'State';
  return REGION_TERMS[country] ?? 'Region';
};

const asString = (v: unknown) => (v as string) ?? '';

// Capitals that most forms list among a country's main subdivisions, e.g. Washington, D.C. or Mexico City.
const CAPITAL_TYPES = ['district', 'federal district', 'capital district', 'capital territory', 'capital city'];

/** A country's main subdivisions (its most common type, e.g. US states), then the rest (e.g. territories). */
const groupSubdivisions = (states: State[]) => {
  const counts: Record<string, number> = {};
  states.forEach(({ type = '' }) => {
    counts[type] = (counts[type] ?? 0) + 1;
  });
  const primaryType = Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0];
  const isPrimary = ({ type = '' }: State) => type === primaryType || CAPITAL_TYPES.includes(type);
  const byName = (a: State, b: State) => a.name.localeCompare(b.name);

  return { primary: states.filter(isPrimary).sort(byName), other: states.filter((s) => !isPrimary(s)).sort(byName) };
};

interface AddressSuggestion {
  id: string;
  mainText: string;
  secondaryText: string;
  prediction: google.maps.places.PlacePrediction;
}

const parseStateCode = (
  stateDetails: { long_name: string; short_name: string } | undefined,
  allStates: State[]
): string => {
  if (!stateDetails?.short_name) return '';
  const match = allStates.find((s) => s.state_code === stateDetails.short_name || s.name === stateDetails.long_name);
  return match?.state_code ?? '';
};

const useAddressAutocomplete = ({
  justUS,
  onSelect,
}: {
  justUS?: boolean;
  onSelect: (address: AddressType) => void;
}) => {
  const { states } = useAddress({ types: ['state'] });
  const addDangerNotification = useNotificationStore((s) => s.addDangerNotification);
  const { loadLibrary, getSessionToken, clearSessionToken, isReady } = useGoogleMapsStore(
    useShallow((s) => ({
      loadLibrary: s.loadLibrary,
      getSessionToken: s.getSessionToken,
      clearSessionToken: s.clearSessionToken,
      isReady: s.libraryStatus.places === LibraryStatus.Loaded,
    }))
  );
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const requestIdRef = useRef(0);

  useEffect(() => {
    loadLibrary(['places']).catch(() => {
      addDangerNotification({ content: 'Failed to load map autocomplete' });
    });
  }, [loadLibrary, addDangerNotification]);

  const clear = useCallback(() => {
    requestIdRef.current++;
    setSuggestions([]);
  }, []);

  const selectSuggestion = useCallback(
    async (suggestion: AddressSuggestion) => {
      requestIdRef.current++;
      setSuggestions([]);
      try {
        const place = suggestion.prediction.toPlace();
        await place.fetchFields({ fields: ['addressComponents'] });
        clearSessionToken();

        if (!place.addressComponents) return;
        const components = place.addressComponents;
        const findComponent = (type: string) => components.find((c) => c.types[0] === type);
        const stateComponent = findComponent('administrative_area_level_1');

        onSelect({
          line1: `${findComponent('street_number')?.longText ?? ''} ${findComponent('route')?.longText ?? ''}`.trim(),
          line2: '',
          city: findComponent('locality')?.longText ?? '',
          state: parseStateCode(
            stateComponent
              ? { long_name: stateComponent.longText ?? '', short_name: stateComponent.shortText ?? '' }
              : undefined,
            states
          ),
          countryCode: findComponent('country')?.shortText ?? '',
          postalCode: findComponent('postal_code')?.longText ?? '',
        });
      } catch (e) {
        console.error('useAddressAutocomplete', e);
      }
    },
    [clearSessionToken, onSelect, states]
  );

  const [fetchSuggestions] = useDebounce(
    async (query: string) => {
      const request: google.maps.places.AutocompleteRequest = {
        input: query,
        sessionToken: getSessionToken(),
      };
      if (justUS) request.includedRegionCodes = ['us'];

      const currentRequestId = ++requestIdRef.current;
      try {
        const { suggestions: results } =
          await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
        if (currentRequestId !== requestIdRef.current) return;

        setSuggestions(
          results
            .map((s) => s.placePrediction)
            .filter((p): p is google.maps.places.PlacePrediction => p !== null)
            .map((prediction) => ({
              id: prediction.placeId,
              mainText: prediction.mainText?.text ?? prediction.text.text,
              secondaryText: prediction.secondaryText?.text ?? '',
              prediction,
            }))
        );
      } catch (e) {
        console.error('useAddressAutocomplete', e);
      }
    },
    300,
    [justUS, getSessionToken]
  );

  const search = useCallback(
    (query: string) => {
      if (!isReady || !query) {
        clear();
        return;
      }
      fetchSuggestions(query);
    },
    [isReady, clear, fetchSuggestions]
  );

  return { isReady, suggestions, search, selectSuggestion, clear };
};

interface AddressLine1Props {
  name: string;
  field: ControllerRenderProps<FieldValues, string>;
  justUS?: boolean;
  disabled?: boolean;
  invalid?: boolean;
}

/**
 * Line 1 input with integrated Google Places autocomplete. The suggestions
 * menu is portalled to `document.body` and fixed-positioned to the input so it
 * always renders above the rest of the form.
 */
const AddressLine1 = ({ name, field, justUS, disabled, invalid }: AddressLine1Props) => {
  const { setValue } = useFormContext();

  const applyAddress = useCallback(
    (a: AddressType) => {
      const opts = { shouldDirty: true, shouldValidate: true };
      setValue(`${name}.line1`, a.line1 ?? '', opts);
      setValue(`${name}.city`, a.city ?? '', opts);
      setValue(`${name}.state`, a.state ?? '', { shouldDirty: true });
      setValue(`${name}.postalCode`, a.postalCode ?? '', { shouldDirty: true });
      setValue(`${name}.countryCode`, a.countryCode ?? '', opts);
    },
    [name, setValue]
  );

  const { isReady, suggestions, search, selectSuggestion } = useAddressAutocomplete({ justUS, onSelect: applyAddress });

  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const blurTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const updateMenuPos = useCallback(() => {
    const rect = wrapRef.current?.getBoundingClientRect();
    if (rect) setMenuPos({ top: rect.bottom + 6, left: rect.left, width: rect.width });
  }, []);

  const menuOpen = open && isReady && suggestions.length > 0;

  useEffect(() => {
    if (!menuOpen) return undefined;
    updateMenuPos();
    window.addEventListener('scroll', updateMenuPos, true);
    window.addEventListener('resize', updateMenuPos);
    return () => {
      window.removeEventListener('scroll', updateMenuPos, true);
      window.removeEventListener('resize', updateMenuPos);
    };
  }, [menuOpen, updateMenuPos]);

  const handlePick = (s: AddressSuggestion) => {
    setOpen(false);
    setActiveIndex(-1);
    void selectSuggestion(s);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      handlePick(suggestions[activeIndex]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapRef} className="relative">
      <Input
        name={field.name}
        value={asString(field.value)}
        onChange={(e) => {
          field.onChange(e);
          search(e.currentTarget.value);
          setActiveIndex(-1);
          setOpen(true);
        }}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
        onBlur={() => {
          field.onBlur();
          blurTimeout.current = setTimeout(() => setOpen(false), 120);
        }}
        onKeyDown={handleKeyDown}
        placeholder="Street address"
        autoComplete="off"
        // 1Password ignores autoComplete="off"; without this its menu takes the arrow keys from the suggestions.
        data-1p-ignore
        disabled={disabled}
        aria-invalid={invalid || undefined}
      />
      {menuOpen &&
        menuPos &&
        createPortal(
          <ul
            role="listbox"
            onMouseDown={(e) => e.preventDefault()}
            style={{ position: 'fixed', top: menuPos.top, left: menuPos.left, width: menuPos.width }}
            className="z-[9999] max-h-72 overflow-y-auto rounded-lg bg-popover p-1 shadow-md ring-1 ring-black/5 dark:ring-white/15"
          >
            {suggestions.map((s, i) => (
              <li
                key={s.id}
                role="option"
                aria-selected={i === activeIndex}
                onMouseEnter={() => setActiveIndex(i)}
                onClick={() => handlePick(s)}
                className={cn(
                  'flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors',
                  i === activeIndex ? 'bg-accent text-accent-foreground' : 'text-gray-600 dark:text-gray-100'
                )}
              >
                <span>{s.mainText}</span>
                {s.secondaryText && <span className="truncate text-gray-400">{s.secondaryText}</span>}
              </li>
            ))}
          </ul>,
          document.body
        )}
    </div>
  );
};

export const AddressFields = ({
  name,
  justUS,
  countryCodes,
  showMapPreview = false,
  required,
  errorLabel,
  heading = 'Address',
  disabled,
  isDisabled,
  fieldRules = {},
}: AddressFieldsProps) => {
  const watched = (useWatch({ name }) as Record<string, string>) || {};
  const country = watched.countryCode || '';

  const { countries } = useAddress({ types: ['country'], countryCodes });
  // Default the region options to the US until a country is chosen, mirroring
  // the rest of the address forms.
  const { states } = useAddress({ types: ['state'], countryCodes: [country || 'US'] });
  const hasStates = states.length > 0;
  const subdivisions = useMemo(() => groupSubdivisions(states), [states]);
  const renderSubdivision = (s: State) => (
    <SelectItem key={`${s.country_code}-${s.state_code}`} value={s.state_code}>
      {s.name}
    </SelectItem>
  );

  const message = (label: string, fallback: string) =>
    errorLabel ? `Please fill out the ${errorLabel} ${label} field` : fallback;
  const req = (label: string, fallback: string) => (required ? { required: message(label, fallback) } : undefined);
  const withRules = (field: keyof AddressType, rules?: RegisterOptions) =>
    rules || fieldRules[field] ? { ...rules, ...fieldRules[field] } : undefined;
  const term = regionTerm(country);

  // Stable between renders: Map re-geocodes whenever its address changes identity.
  const { line1 = '', line2 = '', city = '', state = '', postalCode = '' } = watched;
  const address = useMemo(
    () => ({ line1, line2, city, state, postalCode, countryCode: country }),
    [line1, line2, city, state, postalCode, country]
  );
  const mapReady = !!(address.line1 && address.city && address.countryCode);
  // Waits for typing to settle so the map doesn't geocode and pan on every keystroke.
  const [mapAddress, setMapAddress] = useState(address);
  const [updateMapAddress] = useDebounce(setMapAddress, 500);
  useEffect(() => {
    updateMapAddress(address);
  }, [address, updateMapAddress]);
  // Keeps the narrow layout's map slot open until the map's exit animation finishes.
  const [mapMounted, setMapMounted] = useState(mapReady);
  useEffect(() => {
    if (mapReady) setMapMounted(true);
  }, [mapReady]);
  const reduceMotion = useReducedMotion();
  const mapHidden = { opacity: 0, scale: reduceMotion ? 1 : 0.96 };

  const fields: FormFieldProps<FieldValues>[] = [
    {
      id: `${name}.line1`,
      label: 'Line 1',
      rules: withRules('line1', req('Line 1', 'Address line 1 is required')),
      children: (field, { error }) => (
        <AddressLine1 name={name} field={field} justUS={justUS} disabled={disabled} invalid={!!error} />
      ),
    },
    {
      id: `${name}.line2`,
      label: 'Line 2',
      children: (field, { error }) => (
        <Input
          name={field.name}
          value={asString(field.value)}
          onChange={field.onChange}
          onBlur={field.onBlur}
          aria-invalid={!!error || undefined}
          placeholder="Apt, suite, unit"
          autoComplete="off"
          disabled={disabled}
        />
      ),
    },
    {
      id: `${name}.city`,
      label: `City / ${term}`,
      rules: withRules('city', req('City', 'City is required')),
      children: (field, { error }) => (
        <div className="flex gap-3 @max-[480px]:flex-col">
          <div className="min-w-0 flex-1">
            <Input
              name={field.name}
              value={asString(field.value)}
              onChange={field.onChange}
              onBlur={field.onBlur}
              aria-invalid={!!error || undefined}
              placeholder="City"
              autoComplete="off"
              disabled={disabled}
            />
          </div>
          <div className="min-w-0 flex-1">
            <Controller
              name={`${name}.state`}
              rules={{
                required: false,
                ...withRules(
                  'state',
                  required && justUS ? { required: message('State', 'State is required') } : undefined
                ),
              }}
              render={({ field: stateField, fieldState: { error: stateError } }) =>
                hasStates ? (
                  <Select
                    value={(stateField.value as string) || undefined}
                    onValueChange={stateField.onChange}
                    disabled={disabled}
                  >
                    <SelectTrigger size="lg" className="w-full" aria-invalid={!!stateError || undefined}>
                      <SelectValue placeholder={term} />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      {subdivisions.primary.map(renderSubdivision)}
                      {subdivisions.primary.length > 0 && subdivisions.other.length > 0 && <SelectSeparator />}
                      {subdivisions.other.map(renderSubdivision)}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    name={stateField.name}
                    value={asString(stateField.value)}
                    onChange={stateField.onChange}
                    onBlur={stateField.onBlur}
                    aria-invalid={!!stateError || undefined}
                    placeholder={term}
                    autoComplete="off"
                    disabled={disabled}
                  />
                )
              }
            />
          </div>
        </div>
      ),
    },
    {
      id: `${name}.postalCode`,
      label: 'Postal code',
      rules: withRules(
        'postalCode',
        required && justUS ? { required: message('Postal Code', 'Postal code is required') } : undefined
      ),
      children: (field, { error }) => (
        <Input
          name={field.name}
          value={asString(field.value)}
          onChange={field.onChange}
          onBlur={field.onBlur}
          aria-invalid={!!error || undefined}
          placeholder="Postal code"
          autoComplete="off"
          disabled={disabled}
        />
      ),
    },
    {
      id: `${name}.countryCode`,
      label: 'Country',
      rules: withRules('countryCode', req('Country', 'Country is required')),
      children: (field, { error }) => (
        <Select value={(field.value as string) || undefined} onValueChange={field.onChange} disabled={disabled}>
          <SelectTrigger size="lg" className="w-full" aria-invalid={!!error || undefined}>
            <SelectValue placeholder="Select country..." />
          </SelectTrigger>
          <SelectContent className="max-h-72">
            {countries.map((c) => (
              <SelectItem key={c.code} value={c.code}>
                <CountryFlag country={c.code} size={18} className="rounded-full" />
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ),
    },
  ];

  return (
    <Section headline={heading} isDisabled={isDisabled}>
      <div className="relative grid gap-6 @container/address-fields">
        {fields.map((field) => (
          <FormField key={field.id} {...field} />
        ))}
        {showMapPreview && (
          // Wide layouts leave the fields' last 25% column empty, so the map fills it for the section's full height.
          <div
            className={cn(
              'relative h-42 @wide/address-fields:absolute @wide/address-fields:inset-y-0 @wide/address-fields:right-0 @wide/address-fields:h-auto @wide/address-fields:w-1/4',
              !mapReady && !mapMounted && 'hidden'
            )}
          >
            <AnimatePresence initial={false} onExitComplete={() => setMapMounted(false)}>
              {mapReady && (
                // The map fades itself in once it has drawn; this only animates it away when the address is cleared.
                <motion.div
                  key="map"
                  className="absolute inset-0"
                  initial={false}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={mapHidden}
                  transition={{ duration: 0.2, ease: 'easeOut' }}
                >
                  <Map address={mapAddress} width="100%" height="100%" fadeIn controls />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </div>
    </Section>
  );
};
